// TEMP SEAM — W6-OWNED LIBRARY (W7 spec T3 item 1; dossier W6 Track D item 1).
//
// The shipping virtualization helper is OWNED AND SHIPPED BY W6. W7 consumes
// it by import; because W7 merges before W6, this file is a TEMPORARY local
// seam, clearly marked and vendored under the agreed path + signature so the
// W6 merge REPLACES the file wholesale — no call-site churn, no refactor.
//
// Contract W6 must keep (W7 pins 18-21 depend on it):
//   virtualizeRows({ viewport, rows, overscan, threshold }) -> { destroy() }
//   - rows: in-document row Elements (SSR), all inside one relative host.
//   - Below threshold: NO-OP — every row stays mounted, no scroll observers
//     are attached (pin 20).
//   - At/above threshold: windowed re-parenting — only the visible window
//     plus overscan rows stay MOUNTED; the rest are parked in a detached
//     fragment while fixed-position slots preserve the measured layout
//     heights (mounted row count stays bounded, pin 18). Rows are MOVED,
//     never cloned, and their inline geometry is never touched (pin 19 —
//     percent anchors are scroll-independent).
//   - destroy() restores every row in its original order, removes slots and
//     listeners; a reconnect re-attaches cleanly with no duplication
//     (Turbo-restoration safety, D1 / pin 21).

export const DEFAULT_THRESHOLD = 200
export const DEFAULT_OVERSCAN = 8

export function virtualizeRows({ viewport, rows, overscan = DEFAULT_OVERSCAN, threshold = DEFAULT_THRESHOLD }) {
  if (!viewport || !rows || rows.length === 0) return { destroy() {} }

  // Pin 20: below the threshold render every row and attach no observers.
  if (rows.length < threshold) return { destroy() {} }

  const host = rows[0].parentElement
  if (!host) return { destroy() {} }

  // Measure once at attach (rows are in normal flow at this point) and build
  // prefix sums — the slot tops the window math scrolls against. The prefix
  // sums are row-space (first row at 0); in-flow content ABOVE the rows
  // inside the host (the axis block) shifts every row down by `base`, so
  // slot tops and minHeight are rooted at the first row's flow offset —
  // otherwise virtualization scrolls the axis away and rows render behind it.
  const hostTop = host.getBoundingClientRect().top
  const base = Math.max(0, rows[0].getBoundingClientRect().top - hostTop)
  const offsets = []
  let acc = 0
  const heights = rows.map((row) => {
    const height = row.getBoundingClientRect().height || row.offsetHeight
    offsets.push(acc)
    acc += height
    return height
  })
  const totalHeight = acc

  // Preserve restore order: rows go back in before whichever element
  // originally followed the last row (today-marker overlay, axis siblings).
  const anchor = rows[rows.length - 1].nextSibling
  const parked = document.createDocumentFragment()

  const slots = rows.map((row, index) => {
    const slot = document.createElement("div")
    slot.setAttribute("data-work-virtual-slot", "true")
    slot.style.position = "absolute"
    slot.style.top = `${base + offsets[index]}px`
    slot.style.insetInline = "0"
    slot.appendChild(row)
    return slot
  })

  const hostMinHeight = host.style.minHeight
  host.style.minHeight = `${base + totalHeight}px`
  slots.forEach((slot) => host.appendChild(slot))

  let raf = null
  let first = 0
  let last = -1

  const mountedCount = () => slots.reduce((n, slot) => n + (slot.firstChild ? 1 : 0), 0)

  const renderWindow = () => {
    const scrollTop = viewport.scrollTop
    const visible = viewport.clientHeight || 0

    // Binary search the first row whose bottom edge is below the viewport top.
    // Slot tops carry `base` (in-flow content above the rows), so the
    // viewport comparisons fold it into row-space too.
    let lo = 0
    let hi = rows.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (base + offsets[mid] + heights[mid] < scrollTop) lo = mid + 1
      else hi = mid
    }
    first = lo
    const lastVisible = offsets.reduce(
      (found, offset, index) => (base + offset < scrollTop + visible ? index : found),
      first
    )
    const nextFirst = Math.max(0, first - overscan)
    const nextLast = Math.min(rows.length - 1, lastVisible + overscan)

    if (nextFirst === first && nextLast === last) return
    first = nextFirst
    last = nextLast

    slots.forEach((slot, index) => {
      const inWindow = index >= first && index <= last
      const row = slot.firstChild
      if (inWindow && !row) {
        // Exact lookup by index — park/unpark is per-row, never LIFO.
        const parkedRow = parked.querySelector(`[data-work-virtual-index="${index}"]`)
        if (parkedRow) slot.appendChild(parkedRow)
      } else if (!inWindow && row) {
        parked.appendChild(slot.removeChild(row))
      }
    })
  }

  // Tag rows once so parked lookups are exact even after re-orders.
  rows.forEach((row, index) => row.setAttribute("data-work-virtual-index", String(index)))

  const onScroll = () => {
    if (raf) return
    raf = requestAnimationFrame(() => {
      raf = null
      renderWindow()
    })
  }

  viewport.addEventListener("scroll", onScroll, { passive: true })
  renderWindow()

  return {
    destroy() {
      viewport.removeEventListener("scroll", onScroll)
      if (raf) cancelAnimationFrame(raf)
      raf = null
      // Restore every row in original order, drop the slots, clear layout.
      slots.forEach((slot) => {
        const row = slot.firstChild
        if (row) parked.appendChild(slot.removeChild(row))
        slot.remove()
      })
      const ordered = rows.slice().sort((a, b) => Number(a.getAttribute("data-work-virtual-index")) - Number(b.getAttribute("data-work-virtual-index")))
      ordered.forEach((row) => {
        row.removeAttribute("data-work-virtual-index")
        host.insertBefore(row, anchor)
      })
      host.style.minHeight = hostMinHeight
    },
    // Test seam: mounted row count right now (pin 18).
    _mountedCount: mountedCount,
  }
}
